export default function PrivacyPolicyDocument() {
  return (
    <div className="text-sm text-justify text-gray-700 leading-relaxed max-w-[800px] mx-auto p-5">
      <h1 className="text-xl font-semibold mb-2">EquiPool Privacy Policy</h1>
      <p className="mb-3">Last Updated: January 19, 2026</p>

      <h2 className="text-lg font-semibold mt-6 mb-3">1. Introduction</h2>

      <p className="mb-3">
        EquiPool ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we
        collect, use, disclose, and safeguard your information when you use our website at https://equipool.us and our
        mobile applications (collectively, the "Service").
      </p>

      <p className="mb-3">
        <strong>Important Notice:</strong> EquiPool is a technology platform that facilitates connections between users.
        EquiPool does not provide financial, banking, or escrow services directly. Certain features of the Service rely
        on third-party service providers, which operate independently and under their own privacy policies. Our partners
        maintain their own privacy policies, which govern their data handling practices.
      </p>

      <p className="mb-3">
        By accessing or using our Service, you agree to the collection and use of information in accordance with this
        Privacy Policy. If you do not agree, please do not use the Service.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-3">2. Information We Collect</h2>

      <h3 className="text-base font-semibold mt-4 mb-2">2.1 Personal Information You Provide</h3>

      <p className="mb-2">
        <strong>Account Registration Information</strong>
      </p>
      <ul className="list-disc ml-6 mb-3">
        <li>Full name (first, middle, last)</li>
        <li>Email address</li>
        <li>Phone number (with country code)</li>
        <li>Date of birth</li>
        <li>Password (encrypted; never stored in plain text)</li>
        <li>User role (borrower or investor)</li>
      </ul>

      <p className="mb-2">
        <strong>Investor-Specific Information</strong>
      </p>
      <p className="mb-1">Individual Investors:</p>
      <ul className="list-disc ml-6 mb-3">
        <li>Home address (street, city, state, ZIP code, country)</li>
      </ul>

      <p className="mb-1">Company Investors:</p>
      <ul className="list-disc ml-6 mb-3">
        <li>Company name</li>
        <li>Employer Identification Number (EIN)</li>
        <li>Registered business address</li>
        <li>Company representative information</li>
      </ul>

      <p className="mb-2">
        <strong>Borrower Financial Information</strong>
      </p>
      <ul className="list-disc ml-6 mb-3">
        <li>Mortgage Property address (primary and mailing, if different)</li>
        <li>Property occupancy status</li>
        <li>Property valuation</li>
        <li>Existing loan details and remaining balance</li>
        <li>Requested loan amount</li>
        <li>Preferred return on investment (ROI)</li>
        <li>Loan type and term preferences</li>
        <li>Co-owner information and ownership percentages</li>
        <li>Prior names (if applicable)</li>
      </ul>

      <p className="mb-2">
        <strong>Documents and Files</strong>
      </p>
      <p className="mb-1">We collect documents you upload for identity and property verification, including:</p>
      <ul className="list-disc ml-6 mb-3">
        <li>Government-issued ID (passport or driver's license)</li>
        <li>Property appraisal reports</li>
        <li>Home insurance documents</li>
        <li>Tax returns</li>
        <li>Mortgage statements</li>
        <li>Title deeds</li>
        <li>Property photographs</li>
        <li>Co-owner identification documents</li>
      </ul>

      <p className="mb-2">
        <strong>Verification Codes</strong>
      </p>
      <ul className="list-disc ml-6 mb-3">
        <li>Email verification codes</li>
        <li>Phone verification codes</li>
        <li>Two-factor authentication (2FA) codes</li>
        <li>Qualification codes</li>
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-2">2.2 Information Collected Automatically</h3>

      <p className="mb-2">
        <strong>Authentication Data</strong>
      </p>
      <ul className="list-disc ml-6 mb-3">
        <li>Access tokens</li>
        <li>Refresh tokens</li>
        <li>Session identifiers</li>
        <li>2FA session data</li>
      </ul>

      <p className="mb-2">
        <strong>Device and Usage Information</strong>
      </p>
      <ul className="list-disc ml-6 mb-3">
        <li>IP address</li>
        <li>Browser type and version</li>
        <li>Device type and operating system</li>
        <li>Access dates and times</li>
        <li>Pages viewed and actions taken</li>
      </ul>

      <p className="mb-2">
        <strong>File Metadata</strong>
      </p>
      <ul className="list-disc ml-6 mb-3">
        <li>File name</li>
        <li>File type (MIME type)</li>
        <li>File size</li>
        <li>Upload timestamp</li>
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-2">2.3 Information from Third Parties</h3>

      <p className="mb-2">
        <strong>Identity Verification (Veriff)</strong>
      </p>
      <ul className="list-disc ml-6 mb-3">
        <li>Biometric data for liveness detection</li>
        <li>Government ID verification results</li>
        <li>Verification session data</li>
        <li>Identity verification status</li>
      </ul>

      <p className="mb-2">
        <strong>Payment and Escrow Partners</strong>
      </p>
      <ul className="list-disc ml-6 mb-3">
        <li>Transaction information</li>
        <li>Payment processing data</li>
        <li>Escrow account details</li>
      </ul>

      <h2 className="text-lg font-semibold mt-6 mb-3">3. How We Use Your Information</h2>

      <p className="mb-2">We use your information to:</p>

      <h3 className="text-base font-semibold mt-4 mb-2">3.1 Provide and Manage the Service</h3>
      <ul className="list-disc ml-6 mb-3">
        <li>Create and manage user accounts</li>
        <li>Authenticate identity</li>
        <li>Process loan applications</li>
        <li>Match borrowers with investors</li>
        <li>Facilitate payments and transactions</li>
        <li>Manage escrow services via partners</li>
        <li>Provide customer support</li>
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-2">3.2 Security and Fraud Prevention</h3>
      <ul className="list-disc ml-6 mb-3">
        <li>Verify identity (KYC) using Veriff</li>
        <li>Prevent fraud, abuse, and illegal activity</li>
        <li>Implement two-factor authentication</li>
        <li>Monitor suspicious activity</li>
        <li>Comply with legal and regulatory obligations</li>
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-2">3.3 Communications</h3>
      <ul className="list-disc ml-6 mb-3">
        <li>Send verification emails and SMS messages</li>
        <li>Deliver 2FA codes</li>
        <li>Send transaction and account notifications</li>
        <li>Respond to inquiries and support requests</li>
        <li>Provide administrative updates</li>
      </ul>

      <h4 className="text-sm font-semibold mt-2 mb-1">SMS Communications</h4>
      <p className="mb-2">
        We use your mobile number only to send transactional messages such as one-time passcodes (OTP) and two-factor
        authentication (2FA) codes.
      </p>
      <p className="mb-2">
        Your mobile number will not be sold, rented, or shared with third parties for marketing or promotional purposes.
      </p>
      <p className="mb-3">Msg &amp; data rates may apply. You may opt out at any time by replying STOP.</p>

      <h3 className="text-base font-semibold mt-4 mb-2">3.4 Service Improvement</h3>
      <ul className="list-disc ml-6 mb-3">
        <li>Analyze usage trends</li>
        <li>Improve functionality and performance</li>
        <li>Develop new features</li>
        <li>Conduct internal analytics and research</li>
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-2">3.5 Legal Compliance</h3>
      <ul className="list-disc ml-6 mb-3">
        <li>Comply with applicable laws and regulations</li>
        <li>Enforce Terms of Service</li>
        <li>Protect rights, safety, and property</li>
        <li>Respond to lawful government requests</li>
      </ul>

      <h2 className="text-lg font-semibold mt-6 mb-3">4. How We Share Your Information</h2>

      <h3 className="text-base font-semibold mt-4 mb-2">4.1 Service Providers</h3>

      <p className="mb-2">We share information with trusted third-party providers, including:</p>

      <p className="mb-2">
        <strong>Amazon Web Services (AWS)</strong>
      </p>
      <ul className="list-disc ml-6 mb-3">
        <li>Purpose: Hosting, storage, and infrastructure</li>
        <li>Data Shared: Platform data</li>
        <li>Location: United States</li>
        <li>Security: Encrypted at rest and in transit</li>
      </ul>

      <p className="mb-2">
        <strong>Veriff</strong>
      </p>
      <ul className="list-disc ml-6 mb-3">
        <li>Purpose: Identity and liveness verification</li>
        <li>Data Shared: Government ID, biometric data, verification session data</li>
        <li>Process: User is redirected to Veriff's platform</li>
        <li>Privacy Policy: https://veriff.com/privacy-policy</li>
      </ul>

      <p className="mb-2">
        <strong>Email Service Provider</strong>
      </p>
      <ul className="list-disc ml-6 mb-3">
        <li>Purpose: Emails, notifications, verification codes</li>
        <li>Data Shared: Email address, name, verification codes</li>
      </ul>

      <p className="mb-2">
        <strong>SMS Service Provider</strong>
      </p>
      <ul className="list-disc ml-6 mb-3">
        <li>Purpose: Phone verification and 2FA</li>
        <li>Data Shared: Phone number, verification codes</li>
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-2">Third-Party Service Providers and Their Policies</h3>

      <p className="mb-2">
        We share limited personal information with trusted third-party service providers solely to operate the EquiPool
        platform, including identity verification providers, SMS and email delivery services, cloud infrastructure
        providers, payment processors, escrow companies, and title service providers.
      </p>

      <p className="mb-2">
        Each of these providers operates under its own privacy policy and data protection practices. For more
        information on how these third parties handle your data, please review their respective privacy policies:
      </p>

      <ul className="list-disc ml-6 mb-3">
        <li>
          Veriff (Identity Verification):{' '}
          <a
            href="https://veriff.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            https://veriff.com/privacy-policy
          </a>
        </li>
        <li>
          Cloud Infrastructure (AWS):{' '}
          <a
            href="https://aws.amazon.com/privacy/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            https://aws.amazon.com/privacy/
          </a>
        </li>
        <li>SMS and Messaging Providers: governed by their respective carrier and provider privacy policies</li>
        <li>
          Payment, Escrow, and Title Partners: governed by the privacy policies of the individual escrow and title
          companies involved in your transaction
        </li>
      </ul>

      <p className="mb-2">
        We encourage users to review these third-party privacy policies before engaging in transactions that involve
        them.
      </p>

      <p className="mb-2">
        Your mobile number and personal data are shared with these providers only as necessary to deliver the Service
        (for example, sending one-time passcodes, verifying identity, or completing escrow-related steps). We do not
        sell, rent, or share your personal information for marketing or promotional purposes.
      </p>

      <p className="mb-3">
        EquiPool maintains copies of applicable escrow and title partners’ privacy policies as part of our vendor review
        process to ensure downstream compliance and data protection.
      </p>

      <p className="mb-2">
        <strong>Third-Party Payment and Service Providers</strong>
      </p>
      <ul className="list-disc ml-6 mb-3">
        <li>Purpose: Transaction processing and related services</li>
        <li>Data Shared: Transaction-related data necessary to operate the Service</li>
        <li>Note: These providers operate independently and maintain their own privacy policies</li>
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-2">4.2 Business Transfers</h3>
      <p className="mb-3">
        Your information may be transferred in the event of a merger, acquisition, bankruptcy, or sale of assets. You
        will be notified of any ownership change.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">4.3 Legal Requirements</h3>
      <p className="mb-2">We may disclose information to comply with legal obligations, including:</p>
      <ul className="list-disc ml-6 mb-3">
        <li>Court orders or subpoenas</li>
        <li>Law enforcement requests</li>
        <li>Legal investigations</li>
        <li>Fraud prevention and safety protection</li>
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-2">4.4 With Your Consent</h3>
      <p className="mb-3">We may share information with third parties when you explicitly consent.</p>

      <h3 className="text-base font-semibold mt-4 mb-2">4.5 Aggregated or De-Identified Data</h3>
      <p className="mb-3">We may share aggregated or anonymized data that does not identify individuals.</p>

      <h2 className="text-lg font-semibold mt-6 mb-3">5. Data Storage and Security</h2>

      <h3 className="text-base font-semibold mt-4 mb-2">5.1 Data Storage Location</h3>
      <ul className="list-disc ml-6 mb-3">
        <li>Country: United States</li>
        <li>Cloud Provider: Amazon Web Services (AWS)</li>
        <li>Database: PostgreSQL with backups and redundancy</li>
        <li>File Storage: Encrypted cloud storage</li>
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-2">5.2 Security Measures</h3>
      <p className="mb-2">We use industry-standard safeguards, including:</p>

      <p className="mb-2">
        <strong>Encryption</strong>
      </p>
      <ul className="list-disc ml-6 mb-3">
        <li>Data encrypted at rest</li>
        <li>Data encrypted in transit (TLS 1.2+)</li>
      </ul>

      <p className="mb-2">
        <strong>Access Controls</strong>
      </p>
      <ul className="list-disc ml-6 mb-3">
        <li>Multi-factor authentication</li>
        <li>Role-based access control</li>
        <li>Least-privilege access</li>
      </ul>

      <p className="mb-2">
        <strong>Application Security</strong>
      </p>
      <ul className="list-disc ml-6 mb-3">
        <li>Secure session management</li>
        <li>HTTPS-only connections</li>
        <li>HTTP Strict Transport Security (HSTS)</li>
        <li>Secure password hashing</li>
      </ul>

      <p className="mb-2">
        <strong>Monitoring and Backups</strong>
      </p>
      <ul className="list-disc ml-6 mb-3">
        <li>Continuous monitoring and alerts</li>
        <li>Audit logs</li>
        <li>Automated backups and point-in-time recovery</li>
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-2">5.3 Data Retention</h3>
      <p className="mb-2">We retain information as long as necessary to:</p>
      <ul className="list-disc ml-6 mb-3">
        <li>Maintain active accounts</li>
        <li>Provide services</li>
        <li>Meet legal and regulatory requirements</li>
        <li>Resolve disputes and prevent fraud</li>
      </ul>

      <p className="mb-1">
        <strong>Inactive or Closed Accounts:</strong>
      </p>
      <p className="mb-3">
        Certain data may be retained as required by law or legitimate business purposes, including backups.
      </p>

      <p className="mb-1">
        <strong>Temporary Data:</strong>
      </p>
      <p className="mb-3">Verification codes and session data expire automatically.</p>

      <p className="mb-3">
        You may request data deletion by contacting support@equipool.us, subject to legal requirements.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">5.4 Your Security Responsibilities</h3>
      <p className="mb-2">You are responsible for:</p>
      <ul className="list-disc ml-6 mb-3">
        <li>Using a strong, unique password</li>
        <li>Enabling two-factor authentication</li>
        <li>Keeping credentials confidential</li>
        <li>Logging out on shared devices</li>
        <li>Reporting suspicious activity</li>
      </ul>

      <h2 className="text-lg font-semibold mt-6 mb-3">6. Your Privacy Rights</h2>

      <h3 className="text-base font-semibold mt-4 mb-2">6.1 Access and Portability</h3>
      <ul className="list-disc ml-6 mb-3">
        <li>Access your personal data</li>
        <li>Request a copy of your information</li>
        <li>Understand how your data is processed</li>
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-2">6.2 Correction</h3>
      <ul className="list-disc ml-6 mb-3">
        <li>Update information in your profile</li>
        <li>Request correction of inaccuracies</li>
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-2">6.3 Deletion</h3>
      <ul className="list-disc ml-6 mb-3">
        <li>Request deletion of personal data, subject to legal exceptions</li>
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-2">6.4 Opt-Out</h3>
      <ul className="list-disc ml-6 mb-3">
        <li>Opt out of non-essential notifications</li>
        <li>Essential service communications cannot be disabled</li>
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-2">6.5 State and Regional Rights</h3>
      <p className="mb-1">
        <strong>California (CCPA/CPRA):</strong>
      </p>
      <ul className="list-disc ml-6 mb-3">
        <li>Right to know, delete, and non-discrimination</li>
        <li>We do not sell personal data</li>
      </ul>

      <p className="mb-3">Other U.S. states may provide similar rights.</p>

      <p className="mb-3">To exercise any rights, contact support@equipool.us.</p>

      <h2 className="text-lg font-semibold mt-6 mb-3">7. Cookies and Tracking</h2>

      <p className="mb-3">EquiPool does not use cookies for advertising, analytics, or cross-site tracking.</p>
      <ul className="list-disc ml-6 mb-3">
        <li>Authentication tokens are stored in local storage</li>
        <li>Tokens are used only to maintain sessions</li>
        <li>Tokens are cleared upon logout</li>
      </ul>

      <p className="mb-3">We do not use third-party tracking tools.</p>

      <h2 className="text-lg font-semibold mt-6 mb-3">8. Third-Party Links</h2>

      <p className="mb-2">Our Service may link to third-party platforms, including:</p>
      <ul className="list-disc ml-6 mb-3">
        <li>Veriff identity verification</li>
        <li>Payment processors</li>
        <li>Escrow service providers</li>
      </ul>

      <p className="mb-3">We are not responsible for third-party privacy practices.</p>

      <h2 className="text-lg font-semibold mt-6 mb-3">9. Children's Privacy</h2>

      <p className="mb-3">
        EquiPool is not intended for users under 18. We do not knowingly collect data from children. If such data is
        identified, it will be deleted.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-3">10. International Users</h2>

      <p className="mb-3">
        If you access the Service from outside the United States, your data will be transferred to and stored in the
        U.S. By using the Service, you consent to this transfer.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-3">11. Changes to This Policy</h2>

      <p className="mb-2">We may update this Privacy Policy periodically. Material changes will be communicated by:</p>
      <ul className="list-disc ml-6 mb-3">
        <li>Updating this page</li>
        <li>Revising the "Last Updated" date</li>
        <li>Sending email notifications when appropriate</li>
      </ul>

      <h2 className="text-lg font-semibold mt-6 mb-3">12. Contact Us</h2>

      <p className="mb-2">For questions or requests:</p>
      <p className="mb-1">Email: support@equipool.us</p>
      <p className="mb-3">Response Time: Within 30 days</p>

      <h2 className="text-lg font-semibold mt-6 mb-3">13. Reporting Obligations</h2>

      <ul className="list-disc ml-6 mb-3">
        <li>Reports suspicious activity when legally required</li>
      </ul>

      <h2 className="text-lg font-semibold mt-6 mb-3">14. User Responsibilities</h2>

      <p className="mb-2">By using EquiPool, you agree to:</p>
      <ul className="list-disc ml-6 mb-3">
        <li>Provide accurate information</li>
        <li>Keep your account secure</li>
        <li>Update changes promptly</li>
        <li>Comply with our Terms of Service</li>
      </ul>

      <h2 className="text-lg font-semibold mt-6 mb-3">15. Disclaimer</h2>

      <p className="mb-3">
        While we use strong security measures, no system is completely secure. Use of the Service is at your own risk.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-3">Acknowledgment</h2>

      <p className="mb-3">
        By using EquiPool, you acknowledge that you have read, understood, and agree to this Privacy Policy.
      </p>

      <p className="mb-3">For the most current version, visit: https://equipool.us/privacy</p>

      <hr className="mt-10 mb-5 border-t border-gray-300" />

      <p className="text-center text-xs">© 2024 EquiPool. All rights reserved.</p>
    </div>
  );
}
